import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Form, Input, Select, Typography, Alert } from "antd";
import { LockOutlined, UserOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Text } = Typography;

const SecurityNote = styled(Alert)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

const PortalCredentialModal = ({ visible, onClose, onSave, portal, credential, isSaving, supportedPortals }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEditing = !!credential;

  useEffect(() => {
    if (visible) {
      if (credential) {
        form.setFieldsValue({
          portalId: credential.portalId,
          username: credential.username,
          password: ""
        });
      } else if (portal) {
        form.setFieldsValue({
          portalId: portal.id,
          username: "",
          password: ""
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, credential, portal, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSave(values.portalId, values.username, values.password);
    } catch {
      // Form validation failed
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const title = isEditing ? t("portal_credentials.edit_credential") : t("portal_credentials.add_credential");

  return (
    <Modal
      title={title}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      okText={t("portal_credentials.save")}
      cancelText={t("cancel")}
      confirmLoading={isSaving}
      destroyOnClose
      maskClosable={!isSaving}
      data-testid="portal-credential-modal"
    >
      <SecurityNote
        message={t("portal_credentials.security_note")}
        type="success"
        icon={<SafetyCertificateOutlined />}
        showIcon
      />

      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="portalId"
          label={t("portal_credentials.form_portal_label")}
          rules={[{ required: true, message: t("portal_credentials.validation_portal_required") }]}
        >
          <Select
            placeholder={t("portal_credentials.form_portal_placeholder")}
            disabled={isEditing || !!portal}
            data-testid="portal-select"
          >
            {supportedPortals.map((p) => (
              <Select.Option key={p.id} value={p.id}>
                {p.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="username"
          label={t("portal_credentials.form_username_label")}
          rules={[{ required: true, message: t("portal_credentials.validation_username_required") }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t("portal_credentials.form_username_placeholder")}
            autoComplete="new-username"
            data-testid="username-input"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={t("portal_credentials.form_password_label")}
          rules={[{ required: true, message: t("portal_credentials.validation_password_required") }]}
          extra={isEditing ? <Text type="secondary">{t("portal_credentials.form_password_update_hint")}</Text> : null}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("portal_credentials.form_password_placeholder")}
            autoComplete="new-password"
            data-testid="password-input"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { PortalCredentialModal };
